import i18next from "i18next";

import type { KnownOrderChatToken } from "@ergon-ai/utils/supabase";
import { formatCurrency, getCurrencyScaleFactor } from "@ergon-ai/utils";
import {
  KNOWN_ORDER_CHAT_TOKENS,
  splitOrderChatToken,
} from "@ergon-ai/utils/supabase";

import { getFormattedDateRange } from "./getFormattedDateRange";

const I18N_SYSTEM_MESSAGE = "general.chat.systemMessage";

/**
 * `chat_messages.content` value the BE writes for the purchase system row
 * (inserted on every purchase since the EEAI-74 BE work landed on main).
 * Legacy purchase rows predate the metadata contract (real sender, NULL
 * metadata) — the token still resolves without any metadata.
 */
export const CHAT_ACTIVITY_PURCHASED_TOKEN =
  "activity_purchased" satisfies KnownOrderChatToken;

/**
 * The canonical token list lives in `@ergon-ai/utils/supabase`
 * (KNOWN_ORDER_CHAT_TOKENS, EAH-1931) — re-exported here under this
 * package's historical names for its existing consumers.
 */
export const CHAT_SYSTEM_MESSAGE_TOKENS = KNOWN_ORDER_CHAT_TOKENS;

export type ChatSystemMessageToken = KnownOrderChatToken;

/** Known BE system-message tokens → their translation keys. */
const TOKEN_TRANSLATION_KEYS: Record<ChatSystemMessageToken, string> = {
  activity_purchased: `${I18N_SYSTEM_MESSAGE}.activityPurchased`,
  reschedule_proposed: `${I18N_SYSTEM_MESSAGE}.rescheduleProposed`,
  appointment_updated: `${I18N_SYSTEM_MESSAGE}.appointmentUpdated`,
  activity_approved: `${I18N_SYSTEM_MESSAGE}.activityApproved`,
  activity_scheduled: `${I18N_SYSTEM_MESSAGE}.activityScheduled`,
  activity_rejected: `${I18N_SYSTEM_MESSAGE}.activityRejected`,
  activity_canceled: `${I18N_SYSTEM_MESSAGE}.activityCanceled`,
  activity_completed: `${I18N_SYSTEM_MESSAGE}.activityCompleted`,
  meeting_link_updated: "general.chat.system.meetingLinkUpdated.updated",
};

/** Tokens whose copy can carry a price change (`_withPrice` variants). */
const PRICE_TOKENS = new Set<ChatSystemMessageToken>([
  "reschedule_proposed",
  "appointment_updated",
]);

/**
 * Tokens whose copy can carry an appointment schedule (`_withSchedule`
 * variants). `activity_scheduled` is listed even though the BE writes no
 * schedule in its metadata today (EAH-1885): the moment the BE adds it, the
 * copy upgrades from the generic "appointment confirmed" to the dated one
 * with no FE change.
 */
const SCHEDULE_TOKENS = new Set<ChatSystemMessageToken>([
  "reschedule_proposed",
  "appointment_updated",
  "activity_scheduled",
]);

/** `activity_completed` suffixes with dedicated per-role copy. */
const COMPLETION_ROLES = new Set(["consumer", "professional"]);

/**
 * Machine-token shape: a lowercase snake_case identifier (at least one
 * underscore, no spaces). Human system copy ("Il professionista ha
 * accettato la richiesta") never matches and falls through untouched.
 */
const TOKEN_PATTERN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const parseIsoDate = (value: unknown): string | undefined =>
  typeof value === "string" && !Number.isNaN(new Date(value).getTime())
    ? value
    : undefined;

interface ScheduleParams {
  startAt: string;
  endAt: string;
}

const parseSchedule = (value: unknown): ScheduleParams | undefined => {
  if (!isRecord(value)) return undefined;
  const startAt = parseIsoDate(value.startAt);
  const endAt = parseIsoDate(value.endAt);
  return startAt && endAt ? { startAt, endAt } : undefined;
};

interface PriceParams {
  previousTotal: number;
  newTotal: number;
  currency: string;
}

const parsePrice = (value: unknown): PriceParams | undefined => {
  if (!isRecord(value)) return undefined;
  const { previousTotal, newTotal, currency } = value;
  return typeof previousTotal === "number" &&
    Number.isFinite(previousTotal) &&
    typeof newTotal === "number" &&
    Number.isFinite(newTotal) &&
    typeof currency === "string" &&
    currency !== ""
    ? { previousTotal, newTotal, currency }
    : undefined;
};

/**
 * BE price metadata carries `value`-scale integers (minor units) and the
 * currency, but not the scale factor — derive it from the currency, exactly
 * like the Price GraphQL scalar family does for EUR (2).
 */
const formatPriceValue = (
  value: number,
  currency: string,
  locale: string,
): string =>
  formatCurrency(
    {
      value,
      scaleFactor: getCurrencyScaleFactor(currency, locale),
      currency,
      // A whole-euro price drops its cents, like every price line (formatMoney, 06/10).
      compact: true,
    },
    locale,
  );

/**
 * Looks a key up: the resolved copy, or undefined when the key is missing.
 * Injected so the portal, which runs next-intl and no i18next, can resolve the
 * SAME keys through its own `t` instead of keeping a second copy of this
 * resolver (EAH-2410). Omitted, the ambient i18next instance answers, which is
 * what every native caller does.
 *
 * `undefined` means "fall back", the same contract `NotificationTranslate`
 * uses one file over. Both runtimes render a missing key as its own path —
 * i18next returns the key, next-intl prints it and logs — so a seam that
 * returned a plain string would put `general.chat.systemMessage.…` inside a
 * chat bubble. Every caller wraps its own `t` to answer undefined instead.
 */
export type ChatSystemMessageTranslate = (
  key: string,
  params?: Record<string, string>,
) => string | undefined;

/**
 * The floor under every path, in English, hardcoded.
 *
 * Reached only when nothing resolves at all — an uninitialised i18next, or the
 * fallback key missing from every locale. Without it that case renders the raw
 * key path, which is the thing this helper exists to prevent. Exported because
 * the portal pre-checks unknown tokens itself and needs the same floor.
 */
export const CHAT_SYSTEM_MESSAGE_LAST_RESORT = "Order update";

/**
 * i18next returns the key itself when nothing resolves. Anything empty or
 * key-shaped is treated as absent, exactly as `getNotificationText` does.
 */
const translateWithI18next =
  (lng: string | undefined): ChatSystemMessageTranslate =>
  (key, params) => {
    const value = i18next.t(key, params ? { ...params, lng } : { lng });
    return typeof value === "string" && value.trim() !== "" && value !== key
      ? value
      : undefined;
  };

interface TranslateAttempt {
  key: string;
  params?: Record<string, string>;
}

/**
 * First key that resolves wins, then the generic system line, then English.
 *
 * Every return in this module goes through here. The ladder is what turns a
 * dictionary gap into a slightly plainer sentence rather than a raw key: a
 * missing `_withSchedule` variant falls to the undated copy of the same event,
 * and only a total miss reaches the generic line.
 */
const firstResolved = (
  translate: ChatSystemMessageTranslate,
  attempts: readonly TranslateAttempt[],
): string => {
  for (const { key, params } of attempts) {
    const value = translate(key, params);
    if (value !== undefined && value.trim() !== "") return value;
  }
  const generic = translate(`${I18N_SYSTEM_MESSAGE}.fallback`);
  return generic !== undefined && generic.trim() !== ""
    ? generic
    : CHAT_SYSTEM_MESSAGE_LAST_RESORT;
};

/**
 * Renders an appointment as one string. Injected for the same reason, and it
 * is also where the platforms deliberately differ: native renders
 * "Today"/"Tomorrow" through `getFormattedDateRange`, the portal renders the
 * absolute date. Each caller keeps the rendering it has today.
 */
export type ChatScheduleRangeFormatter = (
  startAt: string,
  endAt: string,
) => string;

const translateKnownToken = (
  token: ChatSystemMessageToken,
  suffix: string | undefined,
  metadata: unknown,
  dates?: Pick<
    GetChatSystemMessageTextOptions,
    | "timeZone"
    | "useAbsoluteDates"
    | "locale"
    | "translate"
    | "formatScheduleRange"
  >,
): string => {
  const key = TOKEN_TRANSLATION_KEYS[token];
  // Passed to every t() below rather than left to the ambient instance. A
  // caller under React Compiler has to be able to make the language a tracked
  // input, and it cannot do that for a read this function makes internally.
  const lng = dates?.locale;
  const translate = dates?.translate ?? translateWithI18next(lng);

  // Completion copy is keyed on WHO confirmed (the content suffix), not on
  // metadata — an unknown/missing suffix degrades to the neutral copy.
  if (token === "activity_completed") {
    const roleKey =
      suffix !== undefined && COMPLETION_ROLES.has(suffix)
        ? [{ key: `${key}_${suffix}` }]
        : [];
    return firstResolved(translate, [...roleKey, { key }]);
  }

  const meta = isRecord(metadata) ? metadata : undefined;
  if (token === "meeting_link_updated") {
    return firstResolved(translate, [
      {
        key: `general.chat.system.meetingLinkUpdated.${meta?.first === true ? "ready" : "updated"}`,
      },
    ]);
  }
  const schedule = SCHEDULE_TOKENS.has(token)
    ? parseSchedule(meta?.schedule)
    : undefined;
  const price = PRICE_TOKENS.has(token) ? parsePrice(meta?.price) : undefined;

  // No usable dynamic data (previews without metadata, legacy rows, BE gaps
  // like EAH-1885's schedule-less activity_scheduled) → parameterless copy.
  if (!schedule && !price) return firstResolved(translate, [{ key }]);

  const locale = lng ?? i18next.language;
  /**
   * `getFormattedDateRange` renders "Today"/"Tomorrow"/the countdown through
   * i18next, and an uninitialised instance answers `undefined` rather than
   * throwing — which lands in the sentence as `undefined - 10:00/11:00`, with
   * no error and no failed test. The two seams are independently optional, so
   * a caller that injects `translate` and forgets this one reaches exactly
   * that. Absolute dates need no lookup, so they are the safe answer whenever
   * the ambient instance cannot be trusted to be the one in use.
   */
  const relativeDatesResolve = i18next.isInitialized && !dates?.translate;
  const formatScheduleRange: ChatScheduleRangeFormatter =
    dates?.formatScheduleRange ??
    ((startAt, endAt) =>
      getFormattedDateRange(
        startAt,
        endAt,
        // Bound to `locale`, not the bare `i18next.t`. This helper renders the
        // "Today"/"Tomorrow" word inside the sentence with its own t() calls,
        // so an unbound one would translate the sentence in the requested
        // language and the date word in the ambient one.
        ((lookup: string, params?: Record<string, unknown>) =>
          i18next.t(lookup, {
            ...params,
            lng: locale,
          })) as typeof i18next.t,
        locale,
        // Both undefined on screen, so the chat keeps rendering the slot in
        // the reader's own zone with its "Today"/"Tomorrow" shorthand.
        {
          timeZone: dates?.timeZone,
          useAbsoluteDates: dates?.useAbsoluteDates ?? !relativeDatesResolve,
        },
      ));

  const params: Record<string, string> = {};
  if (schedule) {
    params.date = formatScheduleRange(schedule.startAt, schedule.endAt);
  }
  if (price) {
    params.previousPrice = formatPriceValue(
      price.previousTotal,
      price.currency,
      locale,
    );
    params.newPrice = formatPriceValue(price.newTotal, price.currency, locale);
  }

  const variant =
    schedule && price
      ? "withSchedulePrice"
      : schedule
        ? "withSchedule"
        : "withPrice";
  // The undated/unpriced copy of the SAME event is the right second choice:
  // a missing variant key loses the detail, never the meaning.
  return firstResolved(translate, [
    { key: `${key}_${variant}`, params },
    { key },
  ]);
};

interface GetChatSystemMessageTextOptions {
  /**
   * Language to translate in. Omit and the ambient i18next instance decides,
   * which is what every caller did before EAH-2135. A memoized caller must
   * pass it: React Compiler caches this call on the arguments it can see, so
   * without the locale among them a language change returns the cached string.
   */
  locale?: string;
  /**
   * Set to true only when the caller KNOWS the row is a system message
   * (`chat_messages.type === "system"`). Only then can an unrecognized
   * machine token (a BE addition this app version does not know yet) be
   * safely replaced with the generic localized fallback.
   *
   * Callers that do NOT have the row type must leave unrecognized content
   * untouched: a user message that happens to be one snake_case word
   * (e.g. someone typing `user_id`) is user text, not a token, and must
   * preview literally. (The notification previews DO have it since
   * `chat_get_unread_counts` gained `last_message_type` — EAH-1910.)
   */
  isSystemMessage?: boolean;
  /**
   * The row's `chat_messages.metadata` JSON, when the caller has it. It
   * supplies the dynamic values for lifecycle copy: `schedule.startAt/endAt`
   * (proposed appointment) and `price.previousTotal/newTotal/currency`
   * (repriced proposal). Accepted as `unknown` and validated field by field —
   * legacy rows (NULL metadata), BE gaps (EAH-1885: `activity_scheduled`
   * writes no schedule yet) and unexpected shapes all degrade to the
   * parameterless copy instead of rendering broken placeholders.
   */
  metadata?: unknown;
  /**
   * IANA zone the schedule dates inside the copy resolve in. OMITTED means
   * the device's own zone, which is what the on-screen chat and the
   * notification previews want: a reader reads times in their own zone.
   *
   * The chat export passes the ORDER's zone instead. Its header ASSERTS one
   * zone for the whole document, so a body dated in the exporting device's
   * zone would make the file contradict itself — the client in Rome reading
   * 16:00 and the professional in Los Angeles reading 07:00 off two
   * documents that both claim Europe/Rome (EAH-2033).
   */
  timeZone?: string;
  /**
   * Forces absolute dates in schedule copy instead of "Today"/"Tomorrow"/the
   * countdown. OMITTED keeps the relative shorthand, which is right on
   * screen and wrong in a file: those labels are relative to the moment of
   * EXPORT, so a stored document silently becomes false the next day, and
   * two people exporting on different days get different text for one slot.
   */
  useAbsoluteDates?: boolean;
  /**
   * Resolves a translation key, answering undefined when it is missing. Omit
   * it and the ambient i18next instance answers, which is what the native
   * apps do.
   */
  translate?: ChatSystemMessageTranslate;
  /**
   * Renders the appointment inside dated copy. Omit it and the i18next-bound
   * `getFormattedDateRange` answers, "Today"/"Tomorrow" included — unless
   * `translate` was injected, which says the caller is not on i18next, and
   * then the default renders the absolute date instead of asking an instance
   * that may never have been initialised.
   */
  formatScheduleRange?: ChatScheduleRangeFormatter;
}

/**
 * Maps a chat message's raw content to display text.
 *
 * The BE stores machine tokens (e.g. `activity_purchased`,
 * `reschedule_proposed:<startAt ISO>`, `activity_completed:<role>`) in
 * `chat_messages.content` for system rows so each client can localize them.
 * Content whose base token (the part before `:`) matches a KNOWN token
 * resolves to its translation regardless of caller — system rows are
 * routinely an activity's last message, so previews must localize them too.
 *
 * UNKNOWN token-shaped content resolves to the generic fallback only when
 * the caller knows the row is system-typed (`isSystemMessage: true`) — a
 * raw `some_new_token` must never render inside the chat. Everywhere else
 * (previews without the row type) it is returned unchanged, as is all
 * non-token content (human copy, regular user messages).
 *
 * Pass `locale` to choose the language. Omitting it falls back to the ambient
 * i18next instance, which is fine for a caller that is not memoized. A
 * memoized one must pass it: React Compiler caches this call on the arguments
 * it can see, so an internally-read language never invalidates (EAH-2135).
 */
export const getChatSystemMessageText = (
  content: string,
  {
    isSystemMessage = false,
    metadata,
    timeZone,
    useAbsoluteDates,
    locale,
    translate,
    formatScheduleRange,
  }: GetChatSystemMessageTextOptions = {},
): string => {
  // Lifecycle tokens can carry an instance suffix after the first `:`
  // (`reschedule_proposed:2026-09-15T14:00:00.000Z`) — match on the base.
  const { baseToken, suffix } = splitOrderChatToken(content);

  // Own-property guard: content is arbitrary user text in previews, and a
  // bare index would resolve Object.prototype members — a user typing
  // "toString" must stay literal, never reach i18next.t as a function.
  if (Object.hasOwn(TOKEN_TRANSLATION_KEYS, baseToken)) {
    return translateKnownToken(
      baseToken as ChatSystemMessageToken,
      suffix,
      metadata,
      { timeZone, useAbsoluteDates, locale, translate, formatScheduleRange },
    );
  }

  // Base-token match so a future suffixed BE token (`new_event:xyz`) still
  // falls back generically on system rows instead of rendering raw.
  if (isSystemMessage && TOKEN_PATTERN.test(baseToken)) {
    return firstResolved(translate ?? translateWithI18next(locale), []);
  }

  return content;
};
